import React, { useState, useRef } from 'react';
import './OverlayManager.css';

export interface Overlay {
  id: string;
  type: 'legend' | 'logo' | 'title';
  visible: boolean;
  position: { x: number; y: number };
  size: { width: number; height: number };
  config: LegendConfig | LogoConfig | TitleConfig;
}

export interface LegendConfig {
  showLocationNames: boolean;
  fontSize: number;
}

export interface LogoConfig {
  logoId: string;
  logoUrl: string;
  fileName: string;
}

export interface TitleConfig {
  text: string;
  fontSize: number;
  fontFamily: string;
}

interface OverlayManagerProps {
  overlays: Overlay[];
  onAddLegend: () => void;
  onAddTitle: (text: string) => void;
  onUploadLogo: (file: File) => Promise<void>;
  onToggleOverlay: (overlayId: string, visible: boolean) => void;
  onRemoveOverlay: (overlayId: string) => void;
  uploadingLogo?: boolean;
  showLocationCircles: boolean;
  onToggleLocationCircles: (show: boolean) => void;
}

export const OverlayManager: React.FC<OverlayManagerProps> = ({
  overlays,
  onAddLegend,
  onAddTitle,
  onUploadLogo,
  onToggleOverlay,
  onRemoveOverlay,
  uploadingLogo = false,
  showLocationCircles,
  onToggleLocationCircles,
}) => {
  const [titleText, setTitleText] = useState<string>('');
  const [showTitleInput, setShowTitleInput] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Validate file before upload
  const validateLogoFile = (file: File): string | null => {
    // Check file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      return 'Invalid file type. Please upload PNG, JPG, or SVG files only.';
    }

    // Check file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      return 'File size exceeds 5MB limit. Please choose a smaller file.';
    }

    return null;
  };

  // Handle logo file selection
  const handleLogoFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadError(null);

    // Validate file
    const validationError = validateLogoFile(file);
    if (validationError) {
      setUploadError(validationError);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    try {
      await onUploadLogo(file);
      // Reset file input on success
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : 'Failed to upload logo');
      // Reset file input on error
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Handle add title
  const handleAddTitle = () => {
    if (titleText.trim()) {
      onAddTitle(titleText.trim());
      setTitleText('');
      setShowTitleInput(false);
    }
  };

  // Handle logo upload button click
  const handleLogoUploadClick = () => {
    fileInputRef.current?.click();
  };

  // Get overlay type display name
  const getOverlayTypeName = (type: string): string => {
    switch (type) {
      case 'legend':
        return 'Legend';
      case 'logo':
        return 'Logo';
      case 'title':
        return 'Title';
      default:
        return type;
    }
  };

  // Get overlay description
  const getOverlayDescription = (overlay: Overlay): string => {
    switch (overlay.type) {
      case 'legend':
        return 'Location legend';
      case 'logo':
        return (overlay.config as LogoConfig).fileName || 'Custom logo';
      case 'title':
        return (overlay.config as TitleConfig).text || 'Map title';
      default:
        return '';
    }
  };

  return (
    <div className="overlay-manager">
      <h3>Overlays</h3>

      {/* Add Overlay Buttons */}
      <div className="overlay-actions">
        <button
          className="overlay-action-btn"
          onClick={onAddLegend}
          title="Add legend showing location names and colors"
        >
          + Add Legend
        </button>

        {!showTitleInput ? (
          <button
            className="overlay-action-btn"
            onClick={() => setShowTitleInput(true)}
            title="Add custom title text to the map"
          >
            + Add Title
          </button>
        ) : (
          <div className="title-input-container">
            <input
              type="text"
              className="title-input"
              placeholder="Enter title text..."
              value={titleText}
              onChange={(e) => setTitleText(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleAddTitle();
                }
              }}
              autoFocus
            />
            <button
              className="title-add-btn"
              onClick={handleAddTitle}
              disabled={!titleText.trim()}
            >
              Add
            </button>
            <button
              className="title-cancel-btn"
              onClick={() => {
                setShowTitleInput(false);
                setTitleText('');
              }}
            >
              Cancel
            </button>
          </div>
        )}

        <button
          className="overlay-action-btn"
          onClick={handleLogoUploadClick}
          disabled={uploadingLogo}
          title="Upload custom logo image (PNG, JPG, or SVG, max 5MB)"
        >
          {uploadingLogo ? 'Uploading...' : '+ Upload Logo'}
        </button>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/jpg,image/svg+xml"
          onChange={handleLogoFileChange}
          style={{ display: 'none' }}
        />
      </div>

      {/* Upload Error */}
      {uploadError && (
        <div className="upload-error">
          <span className="error-icon">⚠️</span>
          {uploadError}
        </div>
      )}

      {/* Overlay List */}
      <div className="overlay-list">
        {overlays.length === 0 ? (
          <div className="no-overlays">
            No overlays added yet. Add a legend, title, or logo to enhance your map.
          </div>
        ) : (
          overlays.map((overlay) => (
            <div key={overlay.id} className="overlay-item">
              <div className="overlay-item-header">
                <label className="overlay-checkbox">
                  <input
                    type="checkbox"
                    checked={overlay.visible}
                    onChange={(e) => onToggleOverlay(overlay.id, e.target.checked)}
                  />
                  <span className="overlay-type">{getOverlayTypeName(overlay.type)}</span>
                </label>
                <button
                  className="overlay-remove-btn"
                  onClick={() => onRemoveOverlay(overlay.id)}
                  title="Remove overlay"
                >
                  ×
                </button>
              </div>
              <div className="overlay-description">
                {getOverlayDescription(overlay)}
              </div>
              {overlay.type === 'logo' && (
                <div className="overlay-preview">
                  <img
                    src={(overlay.config as LogoConfig).logoUrl}
                    alt="Logo preview"
                    className="logo-thumbnail"
                  />
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Instructions */}
      <div className="overlay-instructions">
        <strong>Overlay Instructions:</strong>
        <ul>
          <li>Check/uncheck to show/hide overlays</li>
          <li>Drag overlays on the canvas to reposition</li>
          <li>Overlays will appear in the final print and PDF</li>
          <li>Logo files: PNG, JPG, or SVG (max 5MB)</li>
        </ul>
        
        {/* Location Circles Option */}
        <div className="location-circles-option" style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e0e0e0' }}>
          <label className="overlay-checkbox">
            <input
              type="checkbox"
              checked={showLocationCircles}
              onChange={(e) => onToggleLocationCircles(e.target.checked)}
            />
            <span>Show location circles in PDF preview</span>
          </label>
          <div style={{ fontSize: '12px', color: '#666', marginTop: '4px', marginLeft: '24px' }}>
            When enabled, red circles will mark each location in the PDF preview. 
            The DXF file always includes hole positions for manufacturing.
          </div>
        </div>
      </div>
    </div>
  );
};
